import { Device } from "@twilio/voice-sdk";

let device = null;
let lastIncoming = null;
let activeConnection = null;

export async function initVoiceDevice(clientToken, { iceServers } = {}) {
  if (device) return device;
  if (!clientToken) {
    throw new Error("Missing voice device token");
  }

  device = new Device(clientToken, {
    codecPreferences: ["opus", "pcmu"],
    iceServers: Array.isArray(iceServers) && iceServers.length ? iceServers : undefined,
    logLevel: "error",
  });

  await device.register();

  // Ringback fallback: the carrier's ringback reaches the agent via
  // answerOnBridge on the outbound <Dial>, but if the carrier sends none the
  // caller would hear silence while the far end rings. Explicitly enable the
  // Voice SDK's own outgoing ringtone so there is ALWAYS audible ringing.
  try {
    device.audio?.outgoing?.(true);
    device.audio?.incoming?.(true);
    device.audio?.disconnect?.(true);
  } catch {
    // older SDKs expose these differently; the default is already "on"
  }
  // Expose the device so the in-call overlay can route speaker output.
  try { window.__voiceDevice = device; } catch { /* ignore */ }

  try {
    window.dispatchEvent(new Event("voice-device-registered"));
  } catch {
    // best-effort; safe to ignore
  }
  return device;
}

export function getVoiceDevice() {
  return device;
}

// Call on the FIRST user gesture (first dial): browsers block audio autoplay
// until a gesture, so prime the audio context now so a later INCOMING ring can
// actually sound while the tab is backgrounded — and ask for Notification
// permission once, here, rather than on a cold page load.
let voiceUxPrimed = false;
export function primeVoiceAudio() {
  if (voiceUxPrimed) return;
  voiceUxPrimed = true;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.02);
    }
  } catch {
    /* audio priming is best-effort */
  }
  try {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  } catch {
    /* notifications unsupported */
  }
}

export function destroyVoiceDevice() {
  if (device) {
    device.destroy();
    device = null;
    lastIncoming = null;
    activeConnection = null;
  }
}

export function setLastIncoming(call) {
  lastIncoming = call || null;
}

export function getLastIncoming() {
  return lastIncoming;
}

export function setActiveConnection(connection) {
  activeConnection = connection || null;
}

export function getActiveConnection() {
  return activeConnection;
}
